"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import { useTimezone } from "@/hooks/use-timezone";
import { getIncompleteDependencies } from "@/lib/checklist-dependencies";
import { formatList } from "@/lib/format-data";
import { toMs } from "@/lib/timestamp";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { AppointmentChecklistDependency, AppointmentChecklistItem, PatientStatusEventResponse, PatientStatusEventTiny } from "@/types/event";
import { UserTiny } from "@/types/user";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
import { calendarSelectedLocationFilter } from "@/query/check-in/calendar/location-filter-contract";
import { isPatientStatusChecklistVisible, patientStatusVisibilityScope, usePatientStatusStore } from "@/stores/patient-status-store";
export type PatientStatusRow = {
  id: string;
  event: PatientStatusEventTiny;
  patient: UserTiny;
  serviceTitle?: string;
  lastCheckedItem?: AppointmentChecklistItem;
  nextUpItem?: AppointmentChecklistItem;
  /** Steps that must be completed before `nextUpItem` can be. */
  nextUpBlockedBy?: AppointmentChecklistDependency[];
};
function getPatient(event: PatientStatusEventTiny): UserTiny | undefined {
  return event.attendees.find(a => a.type === "PATIENT");
}
type PatientStatusConfig = {
  label: string;
  isPrivate: boolean;
};
const baseStatusTypes: Partial<Record<AppointmentChecklistType, PatientStatusConfig>> = {
  [AppointmentChecklistType.WITH_MA]: {
    isPrivate: false,
    label: "Waiting for MA"
  },
  [AppointmentChecklistType.MEASUREMENT_TAKEN]: {
    isPrivate: true,
    label: "Taking Measurements"
  },
  [AppointmentChecklistType.WITH_PROVIDER]: {
    isPrivate: false,
    label: "Waiting for Provider"
  },
  [AppointmentChecklistType.NOTE_TAKEN]: {
    isPrivate: true,
    label: "Taking Notes"
  }
};
export const patientStatusTypes: Partial<Record<AppointmentChecklistType, PatientStatusConfig>> = {
  [AppointmentChecklistType.CHECKED_IN]: {
    isPrivate: false,
    label: "Checked In"
  },
  ...baseStatusTypes
};
export const nextUpTypes: Partial<Record<AppointmentChecklistType, PatientStatusConfig>> = {
  ...baseStatusTypes,
  [AppointmentChecklistType.PAYMENT_MADE]: {
    isPrivate: false,
    label: "Waiting for Payment"
  },
  [AppointmentChecklistType.REBOOK]: {
    isPrivate: false,
    label: "Rebooking"
  }
};

/** Time (ms) after check-in before a patient is considered overdue. */
const CHECKED_IN_OVERDUE_THRESHOLD_MS = 2 * 60 * 1000;
/** Time (ms) after any other checklist step before a patient is considered overdue. */
const DEFAULT_OVERDUE_THRESHOLD_MS = 5 * 60 * 1000;
function isRelevantItem(item: AppointmentChecklistItem) {
  return item.showInPatientStatus === true;
}
function compareChecklistItems(a: AppointmentChecklistItem, b: AppointmentChecklistItem) {
  return a.position - b.position;
}
function findLastCheckedBefore(items: AppointmentChecklistItem[], nextUpItem: AppointmentChecklistItem) {
  return items.filter(item => item.completedDate && compareChecklistItems(item, nextUpItem) < 0).sort((a, b) => compareChecklistItems(b, a))[0];
}
function getNextUpItem(checklists: AppointmentChecklistItem[]) {
  // Next up is always the earliest incomplete step shown in Patient Status. No
  // step is skippable by completing a later one: measurements get recorded from
  // the appointment sidebar and forms auto-complete before the patient arrives,
  // so a completion further down the list says nothing about the steps before it.
  return checklists
  // Visual locks never invalidate recorded completion for this board.
  .filter(item => isRelevantItem(item) && !item.completedDate).sort(compareChecklistItems)[0];
}
function measurementStatusLabel(name: string) {
  const trimmed = name.trim();
  const match = trimmed.match(/^take\s+(.+)$/i);
  if (match?.[1]) {
    return `${match[1].replace(/\s+$/, "")} taken`;
  }
  return trimmed || nextUpTypes[AppointmentChecklistType.MEASUREMENT_TAKEN]?.label || "Measurement taken";
}
function rowGroupKey(row: PatientStatusRow) {
  const type = row.nextUpItem?.type;
  if (type === AppointmentChecklistType.MEASUREMENT_TAKEN) {
    const item = row.nextUpItem;
    const targetId = item?.measurementFieldId ?? item?.measurementGroupId;
    const stageKey = item ? [targetId ?? item.id ?? row.id, item.name.trim().toLowerCase(), item.optional].join(":") : row.id;
    return `${type}:${stageKey}`;
  }
  return type ? String(type) : `CUSTOM:${row.nextUpItem?.name.trim().toLowerCase() || row.id}`;
}
function rowGroupLabel(row: PatientStatusRow, key: string) {
  if (row.nextUpItem?.type === AppointmentChecklistType.MEASUREMENT_TAKEN) {
    return measurementStatusLabel(row.nextUpItem.name);
  }
  return nextUpTypes[row.nextUpItem?.type as AppointmentChecklistType]?.label ?? row.nextUpItem?.name ?? key;
}

/** Group rows by nextUpItem status and sort each group by completedDate. */
function groupRows(rows: PatientStatusRow[]) {
  const byGroup: Record<string, PatientStatusRow[]> = {};
  for (const row of rows) {
    const key = rowGroupKey(row);
    if (!byGroup[key]) byGroup[key] = [];
    byGroup[key].push(row);
  }
  for (const key of Object.keys(byGroup)) {
    byGroup[key].sort((a, b) => {
      if (a.lastCheckedItem?.completedDate && b.lastCheckedItem?.completedDate) {
        return toMs(a.lastCheckedItem.completedDate) - toMs(b.lastCheckedItem.completedDate);
      }
      return 0;
    });
  }
  const knownKeys = Object.keys(nextUpTypes).flatMap(typeKey => {
    if (typeKey !== AppointmentChecklistType.MEASUREMENT_TAKEN) {
      return byGroup[typeKey]?.length > 0 ? [typeKey] : [];
    }
    return Object.keys(byGroup).filter(key => key.startsWith(`${AppointmentChecklistType.MEASUREMENT_TAKEN}:`)).sort((a, b) => compareChecklistItems(byGroup[a][0].nextUpItem!, byGroup[b][0].nextUpItem!));
  });
  const remainingKeys = Object.keys(byGroup).filter(key => !knownKeys.includes(key)).sort((a, b) => compareChecklistItems(byGroup[a][0].nextUpItem!, byGroup[b][0].nextUpItem!));
  const orderedKeys = [...knownKeys, ...remainingKeys];
  const index = orderedKeys.map(key => ({
    key,
    label: rowGroupLabel(byGroup[key][0], key),
    total: byGroup[key].length,
    rows: byGroup[key]
  }));
  return {
    byGroup,
    index,
    count: rows.length
  };
}
export function usePatientStatus() {
  const $ = _c(37);
  const checklistVisibility = usePatientStatusStore(_temp);
  const checklistVisibilityDefaults = usePatientStatusStore(_temp2);
  const {
    tenant
  } = useTenant();
  const {
    currentUser,
    currentLocationId
  } = useUser();
  const t0 = tenant?.subDomain;
  const t1 = currentUser?.id;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = patientStatusVisibilityScope(t0, t1);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const visibilityScope = t2;
  const isModuleEnabled = useModule("CALENDAR_PATIENT_STATUS");
  const timezone = useTimezone();
  const visualDependenciesEnabled = useModule("CHECKLIST_DEPENDENCIES");
  let t3;
  if ($[3] !== currentLocationId) {
    t3 = calendarSelectedLocationFilter(currentLocationId ? [currentLocationId] : []);
    $[3] = currentLocationId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const locationFilter = t3;
  let t4;
  if ($[5] !== isModuleEnabled || $[6] !== locationFilter || $[7] !== timezone) {
    t4 = eventQueryOptions.patientStatus(locationFilter, timezone, isModuleEnabled);
    $[5] = isModuleEnabled;
    $[6] = locationFilter;
    $[7] = timezone;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: patientStatusData,
    isLoading
  } = useQuery(t4);
  const [tick, setTick] = useState(0);
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const id = setInterval(() => {
        setTick(_temp3);
      }, 60000);
      return () => clearInterval(id);
    };
    t6 = [];
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const isOverdue = _temp4;
  let t7;
  if ($[11] !== currentUser?.id || $[12] !== patientStatusData || $[13] !== visualDependenciesEnabled) {
    t7 = () => {
      if (!patientStatusData) {
        return [];
      }
      const rows = [];
      patientStatusData.forEach(item => {
        const evt = item.event;
        const checklists = item.checklist;
        if (!evt || !checklists || checklists.length === 0) {
          return;
        }
        const nextUpItem = getNextUpItem(checklists);
        if (!nextUpItem) {
          return;
        }
        const lastCheckedItem = findLastCheckedBefore(checklists, nextUpItem) ?? checklists[0];
        if (nextUpItem.type && nextUpTypes[nextUpItem.type]?.isPrivate) {
          if (lastCheckedItem?.completedByUser?.id !== currentUser?.id) {
            return;
          }
        }
        const patient = getPatient(evt);
        if (!patient) {
          return;
        }
        rows.push({
          id: `${evt.id}:${nextUpItem.id}`,
          event: evt,
          patient,
          serviceTitle: formatList(evt.serviceNames),
          lastCheckedItem,
          nextUpItem,
          nextUpBlockedBy: visualDependenciesEnabled ? getIncompleteDependencies(nextUpItem, checklists) : []
        });
      });
      return rows;
    };
    $[11] = currentUser?.id;
    $[12] = patientStatusData;
    $[13] = visualDependenciesEnabled;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  currentUser?.id;
  let t8;
  if ($[15] !== t7) {
    t8 = t7();
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const allRows = t8;
  let t9;
  if ($[17] !== patientStatusData) {
    t9 = Array.from(new Set((patientStatusData ?? []).flatMap(_temp6))).sort(_temp7);
    $[17] = patientStatusData;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const availableChecklistNames = t9;
  let t10;
  if ($[19] !== allRows || $[20] !== checklistVisibility || $[21] !== checklistVisibilityDefaults || $[22] !== visibilityScope) {
    const filtered = allRows.filter(row_0 => !visibilityScope || isPatientStatusChecklistVisible(checklistVisibility, checklistVisibilityDefaults, visibilityScope, row_0.nextUpItem.name));
    t10 = groupRows(filtered);
    $[19] = allRows;
    $[20] = checklistVisibility;
    $[21] = checklistVisibilityDefaults;
    $[22] = visibilityScope;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const {
    byGroup,
    index,
    count
  } = t10;
  let t11;
  if ($[24] !== byGroup || $[25] !== count || $[26] !== index) {
    t11 = {
      rowsByGroup: byGroup,
      groupIndex: index,
      numEntries: count
    };
    $[24] = byGroup;
    $[25] = count;
    $[26] = index;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const {
    rowsByGroup,
    groupIndex,
    numEntries
  } = t11;
  let t12;
  if ($[28] !== allRows) {
    t12 = groupRows(allRows);
    $[28] = allRows;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const allGroupIndex = t12.index;
  let t13;
  if ($[30] !== allGroupIndex || $[31] !== availableChecklistNames || $[32] !== groupIndex || $[33] !== isLoading || $[34] !== numEntries || $[35] !== rowsByGroup) {
    t13 = {
      groupIndex,
      allGroupIndex,
      rowsByGroup,
      isLoading,
      numEntries,
      availableChecklistNames,
      isOverdue
    };
    $[30] = allGroupIndex;
    $[31] = availableChecklistNames;
    $[32] = groupIndex;
    $[33] = isLoading;
    $[34] = numEntries;
    $[35] = rowsByGroup;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}
function _temp7(a, b) {
  return a.localeCompare(b);
}
function _temp6(item_0) {
  return item_0.checklist.filter(isRelevantItem).map(_temp5);
}
function _temp5(checklistItem) {
  return checklistItem.name;
}
function _temp4(row) {
  const now = Date.now();
  if (now > toMs(row.event.end)) {
    return true;
  }
  if (!row.lastCheckedItem?.completedDate) {
    return false;
  }
  const threshold = row.lastCheckedItem.type === AppointmentChecklistType.CHECKED_IN ? CHECKED_IN_OVERDUE_THRESHOLD_MS : DEFAULT_OVERDUE_THRESHOLD_MS;
  return now - toMs(row.lastCheckedItem.completedDate) > threshold;
}
function _temp3(prev) {
  return prev + 1;
}
function _temp2(state_0) {
  return state_0.checklistVisibilityDefaults;
}
function _temp(state) {
  return state.checklistVisibility;
}